import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../auth/authService";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
} from "@mui/material";


export default function Login() {
  const navigate = useNavigate();
  const cardRef = useRef(null);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [thisVersion, setThisVersion] = useState("");
  useEffect(() => {
    const version = process.env.REACT_APP_VERSION;
    document.title = `BetaniaHub | v ${version}`;
    setThisVersion(version);
  }, []);


  /** 3D Tilt Effect */
  useEffect(() => {
    const card = cardRef.current;
    if (!card) return;

    const handleMove = (e) => {
      const rect = card.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width;
      const y = (e.clientY - rect.top) / rect.height;

      const tiltX = (y - 0.5) * 10;
      const tiltY = (x - 0.5) * -10;

      card.style.transform = `rotateX(${tiltX}deg) rotateY(${tiltY}deg)`;
    };

    const resetTilt = () => {
      card.style.transform = "rotateX(0deg) rotateY(0deg)";
    };

    card.addEventListener("mousemove", handleMove);
    card.addEventListener("mouseleave", resetTilt);

    return () => {
      card.removeEventListener("mousemove", handleMove);
      card.removeEventListener("mouseleave", resetTilt);
    };
  }, []);
  //https://images.unsplash.com/photo-1732029062269-dab8feace9f3
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await AuthService.login(username, password);
      // console.log('Login response: ', resp);
      navigate("/dashboard");
    } catch (err) {
      setError("Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        backgroundImage: `url("https://images.unsplash.com/photo-1486520299386-6d106b22014b")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(120deg, rgba(2,6,23,0.75), rgba(30,41,59,0.65), rgba(15,23,42,0.8))",
          zIndex: 0,
        },
      }}
    >
      <Box
        ref={cardRef}
        sx={{
          position: "relative",
          zIndex: 1,
          width: "min(92vw, 440px)",
          p: { xs: 3, md: 4 },
          borderRadius: 4,
          backgroundColor: "rgba(255,255,255,0.9)",
          border: "1px solid rgba(255,255,255,0.6)",
          boxShadow: "0 18px 45px rgba(2,6,23,0.35)",
          backdropFilter: "blur(12px)",
        }}
      >
        {/* Header */}
        <Box mb={4}>
          <Stack direction="row" spacing={0} alignItems="center">
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                letterSpacing: 1,
                background:
                  "linear-gradient(90deg,#38bdf8,#2563eb)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Betania
            </Typography>
            <Typography variant="h3"
              sx={{
                fontWeight: 700,
                letterSpacing: 1,
                background:
                  "#0f172a",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}>Hub</Typography>

          </Stack>
          <Typography
            variant="body2"
            color="text.secondary"
            mt={0.75}
          >
            Management & Payment System
          </Typography>

          <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
            <Typography
              variant="caption"
              sx={{
                px: 1,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: "rgba(37,99,235,0.12)",
                color: "#2563eb",
                fontWeight: 600,
              }}
            >
              {thisVersion}
            </Typography>
          </Stack>
        </Box>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Form */}
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            margin="normal"
          />

          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
          />

          <Typography>{error}</Typography>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{
              mt: 3,
              py: 1.3,
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(90deg,#2563eb,#1d4ed8)",
              boxShadow: "0 10px 22px rgba(37,99,235,0.35)",
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Sign In"
            )}
          </Button>
        </Box>

        {/* Footer */}
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          textAlign="center"
          mt={4}
        >
          © {new Date().getFullYear()} BetaniaHub. All rights reserved.
        </Typography>
      </Box>
    </Box>
  );

}
